import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import IndividualGAD7Form from './IndividualGAD7Form';
import IndividualGAD7Chart, { processClientData } from './IndividualGAD7Chart';
import IndividualGAD7Analytics from './IndividualGAD7Analytics';
import IndividualGAD7PDF from './IndividualGAD7PDF';
import { analyticsDataService } from '../../../services/analyticsDataService';

const CHART_TYPE = 'individual-gad7-tracker';

const IndividualGAD7Tracker = ({ user, viewData = null, readOnly = false }) => {
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const pdfChartRef = useRef(null);
  const [view, setView] = useState(viewData ? 'chart' : 'select');
  const [savedData, setSavedData] = useState(
    viewData ? { data: viewData, timestamp: new Date().toISOString(), recordCount: viewData.length } : null
  );
  const [hasExistingData, setHasExistingData] = useState(!!(viewData && viewData.length));
  const [selectedClient, setSelectedClient] = useState('');

  // Get unique client IDs from saved data
  const clientIds = useMemo(() => {
    if (!savedData?.data) return [];
    return [...new Set(savedData.data.map(d => d.clientId))].sort();
  }, [savedData]);

  // Auto-select first client when data loads
  useEffect(() => {
    if (clientIds.length > 0 && !selectedClient) {
      setSelectedClient(clientIds[0]);
    }
  }, [clientIds, selectedClient]);

  // Process data for selected client
  const clientData = useMemo(() => {
    if (!savedData?.data || !selectedClient) return null;
    return processClientData(savedData.data, selectedClient);
  }, [savedData, selectedClient]);

  useEffect(() => {
    if (viewData) return; // read-only supervisor view — data is injected via props
    if (user?.id) {
      analyticsDataService.setCurrentUser(user.id);
    }
    checkExistingData();

    // Cloud sync: pull newer data from cloud
    if (user?.id) {
      analyticsDataService.syncFromCloud(CHART_TYPE).then(cloudData => {
        if (cloudData) {
          setSavedData(cloudData);
          setHasExistingData(true);
        }
      });
    }
  }, [user, viewData]);

  const checkExistingData = () => {
    const existing = analyticsDataService.hasChartData(CHART_TYPE);
    setHasExistingData(existing);
    if (existing) {
      const data = analyticsDataService.getChartData(CHART_TYPE);
      setSavedData(data);
    }
  };

  const handleFormSave = (data) => {
    const result = analyticsDataService.saveChartData(CHART_TYPE, data);
    if (result.success) {
      setSavedData({ data, timestamp: result.timestamp, recordCount: result.recordCount });
      setHasExistingData(true);
      // Auto-select first client
      const clients = [...new Set(data.map(d => d.clientId))].sort();
      if (clients.length > 0) setSelectedClient(clients[0]);
      setView('chart');
    }
  };

  const handleFormCancel = () => {
    setView('select');
  };

  const needsClientSelection = (view === 'chart' || view === 'analytics') && clientIds.length > 0;

  return (
    <div className="h-full overflow-y-auto p-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        {!readOnly && (
        <button
          onClick={() => navigate('/analytics')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <span className="mr-2">←</span>
          Back to Charts
        </button>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Individual Client GAD-7 Tracker</h1>
          <p className="text-gray-600 mt-2">FDA-Compliant Longitudinal Anxiety Monitoring with Evidence-Based Clinical Decision Support</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-6">
          {!readOnly && (
          <button
            onClick={() => setView('form')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              view === 'form'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Enter New Data
          </button>
          )}
          <button
            onClick={() => setView('chart')}
            disabled={!hasExistingData}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              view === 'chart'
                ? 'bg-blue-600 text-white shadow-md'
                : !hasExistingData
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Visualize Data
            {hasExistingData && savedData && (
              <span className="ml-2 text-xs opacity-75">({savedData.recordCount} records)</span>
            )}
          </button>
          <button
            onClick={() => setView('analytics')}
            disabled={!hasExistingData}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              view === 'analytics'
                ? 'bg-blue-600 text-white shadow-md'
                : !hasExistingData
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            View Analytics
          </button>
          <IndividualGAD7PDF
            clientData={clientData}
            chartRef={pdfChartRef}
            user={user}
          />
        </div>

        {/* Client Selector - shown when viewing chart or analytics */}
        {needsClientSelection && (
          <div className="bg-white rounded-xl shadow-md p-4 mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Client</label>
            {clientIds.length === 1 ? (
              <p className="text-blue-700 font-semibold">Analyzing data for: {clientIds[0]}</p>
            ) : (
              <select
                value={selectedClient}
                onChange={e => setSelectedClient(e.target.value)}
                className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold"
              >
                {clientIds.map(id => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </select>
            )}
            {clientData && (
              <p className="text-xs text-gray-400 mt-1">
                {clientData.assessmentCount} assessments | {clientData.durationDays} days | Current: {clientData.currentSeverity}
              </p>
            )}
            {!clientData && selectedClient && (
              <p className="text-xs text-orange-500 mt-1">
                This client needs at least 2 GAD-7 assessments for tracking.
              </p>
            )}
          </div>
        )}

        {/* Content Area */}
        {view === 'select' && (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <p className="text-gray-500">Select an option above to get started</p>
            <p className="text-sm text-gray-400 mt-2">
              Enter multiple GAD-7 scores per client over time to track anxiety trajectories.
            </p>
            {hasExistingData && savedData && (
              <p className="text-sm text-gray-400 mt-2">
                Last updated: {new Date(savedData.timestamp).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {view === 'form' && (
          <IndividualGAD7Form
            onSave={handleFormSave}
            onCancel={handleFormCancel}
            existingData={savedData?.data || []}
          />
        )}

        {view === 'chart' && clientData && (
          <div>
            <div ref={chartRef}>
              <IndividualGAD7Chart clientData={clientData} />
            </div>
            <p className="text-sm text-gray-400 mt-4 text-center">
              {clientData.assessmentCount} assessments | Last updated: {new Date(savedData.timestamp).toLocaleString()}
            </p>
          </div>
        )}

        {view === 'analytics' && clientData && (
          <div>
            <IndividualGAD7Analytics clientData={clientData} />
            <p className="text-sm text-gray-400 mt-4 text-center">
              {clientData.assessmentCount} assessments | Last updated: {new Date(savedData.timestamp).toLocaleString()}
            </p>
          </div>
        )}

        {/* Hidden chart for PDF capture */}
        {clientData && (
          <div
            ref={pdfChartRef}
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '1200px',
              minHeight: '600px'
            }}
            aria-hidden="true"
          >
            <IndividualGAD7Chart clientData={clientData} showForecast={true} chartHeight={500} pdfMode={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default IndividualGAD7Tracker;
